import type { PanchangaYearDay } from '../services/api';
import type {
  PanchangaDayRecord,
  PanchangaDetailsDto,
  PanchangaCoordinatesDto,
  BikramSambatDto,
} from '../types/panchanga';

export type ElibPanchangaRow = PanchangaDayRecord | {
  bikram_sambat?: BikramSambatDto | {
    date_str?: string;
    day?: number;
    month?: number;
    year?: number;
  };
  panchanga?: PanchangaDetailsDto | Record<string, unknown>;
  coordinates?: PanchangaCoordinatesDto | Record<string, unknown>;
};

export type PanchangaKaalaPeriod = {
  name: string;
  /** Object key in day_length_based_periods, e.g. `aparaahna`. */
  key?: string;
  /** Local civil datetime, e.g. `2026-03-15 06:17:27`. */
  start: string;
  end: string;
};

const NESTED_DIVISION_KEYS = new Set(['eight_fold_division', 'fifteen_fold_division']);

const INSTANT_KAALA_FIELDS: Array<{ name: string; field: string }> = [
  { name: 'सूर्योदयः', field: 'sunrise' },
  { name: 'सूर्यास्तमयः', field: 'sunset' },
  { name: 'चन्द्रोदयः', field: 'moonrise' },
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

type PanchangaEventRow = {
  kind?: string;
  index?: number;
  name?: string;
  pakshya?: string;
};

const isPakshyaKrishna = (pakshya?: string | null): boolean => {
  if (!pakshya || typeof pakshya !== 'string') return false;
  const s = pakshya.trim().toLowerCase();
  return s === 'krishna' || s.includes('कृष्ण');
};

/** Tithi index at sunrise (`panchanga.tithi_index` normalized to 1..30). */
export function suryodayaTithiIndex(day: PanchangaYearDay | undefined): number | undefined {
  if (!day) return undefined;
  const raw = Number(day.tithi_index);
  const n = Number.isFinite(raw) && raw > 0 ? raw : undefined;
  const candidate = n ?? extractEventTithiIndexes(day)[0];
  if (candidate == null) return undefined;
  if (candidate <= 15 && isPakshyaKrishna(day.pakshya)) {
    return candidate + 15;
  }
  return candidate;
}

/** Distinct tithi indexes from `panchanga.events` (kind === tithi, normalized to 1..30). */
export function extractEventTithiIndexes(day: PanchangaYearDay | undefined): number[] {
  if (!day) return [];
  const events = day.events;
  if (!Array.isArray(events)) return [];
  const isKrishna = isPakshyaKrishna(day.pakshya);
  const indexes = events
    .filter((item): item is PanchangaEventRow => Boolean(item) && typeof item === 'object')
    .filter((item) => item.kind === 'tithi')
    .map((item) => {
      const idx = Number(item.index);
      if (!Number.isFinite(idx) || idx <= 0) return NaN;
      if (idx <= 15 && (isPakshyaKrishna(item.pakshya) || (!item.pakshya && isKrishna))) return idx + 15;
      return idx;
    })
    .filter((n) => Number.isFinite(n) && n > 0);
  return Array.from(new Set(indexes));
}

/** Default dateIn when prefilling from a panchanga day. */
export function defaultDateInFromPanchanga(day: PanchangaYearDay | undefined): number | undefined {
  const eventTithis = extractEventTithiIndexes(day);
  if (eventTithis.length === 0) return undefined;
  const suryodaya = suryodayaTithiIndex(day);
  if (suryodaya != null && eventTithis.includes(suryodaya)) return suryodaya;
  return eventTithis[0];
}

export function mapElibPanchangaRow(
  row: ElibPanchangaRow | PanchangaYearDay | Record<string, unknown>,
): PanchangaYearDay {
  if (!row || typeof row !== 'object') {
    return {} as PanchangaYearDay;
  }
  const hasNestedPanchanga = isRecord((row as ElibPanchangaRow).panchanga);
  const panchanga = hasNestedPanchanga
    ? ((row as ElibPanchangaRow).panchanga as Record<string, unknown>)
    : (row as Record<string, unknown>);
  const bs = (row as ElibPanchangaRow).bikram_sambat;
  const events = Array.isArray(panchanga.events)
    ? (panchanga.events as unknown[])
    : Array.isArray((row as Record<string, unknown>).events)
      ? ((row as Record<string, unknown>).events as unknown[])
      : [];
  const firstTithi = events.find(
    (item) => isRecord(item) && item.kind === 'tithi' && Number.isFinite(Number(item.index)),
  ) as { index?: number } | undefined;
  const derivedTithiIndex =
    Number(panchanga.tithi_index ?? (row as Record<string, unknown>).tithi_index) ||
    (firstTithi ? Number(firstTithi.index) : undefined);
  const coords =
    (row as Record<string, unknown>).coordinates ??
    (panchanga as Record<string, unknown>).coordinates;

  return {
    ...(row as Record<string, unknown>),
    ...panchanga,
    tithi_index: derivedTithiIndex ?? panchanga.tithi_index,
    date:
      typeof panchanga.date === 'string'
        ? panchanga.date
        : typeof (row as Record<string, unknown>).date === 'string'
          ? (row as Record<string, unknown>).date
          : '',
    bs_year: Number(panchanga.bs_year ?? (row as Record<string, unknown>).bs_year ?? bs?.year) || 0,
    bs_month: Number(panchanga.bs_month ?? (row as Record<string, unknown>).bs_month ?? bs?.month) || 0,
    day_number:
      Number(panchanga.day_number ?? (row as Record<string, unknown>).day_number ?? bs?.day) || 0,
    coordinates: coords,
  } as PanchangaYearDay;
}

export function normalizeKaalaName(value: string): string {
  return value
    .normalize('NFC')
    .replace(/[\u200c\u200d]/g, '')
    .replace(/[ः:]/g, '')
    .replace(/~/g, '')
    .replace(/[()]/g, '')
    .replace(/\s+/g, '')
    .trim()
    .toLowerCase();
}

/**
 * Sanskrit kaala labels → `day_length_based_periods` object keys.
 * Lookup prefers fifteen_fold_division, then eight_fold_division, then top-level.
 */
const KAALA_NAME_TO_KEY: Record<string, string> = {
  [normalizeKaalaName('अपराह्णः')]: 'aparaahna',
  [normalizeKaalaName('अपराह्नः')]: 'aparaahna',
  [normalizeKaalaName('aparaahna')]: 'aparaahna',
  [normalizeKaalaName('aparahna')]: 'aparaahna',
  [normalizeKaalaName('पूर्वाह्णः')]: 'puurvaahna',
  [normalizeKaalaName('पूर्वाह्नः')]: 'puurvaahna',
  [normalizeKaalaName('puurvaahna')]: 'puurvaahna',
  [normalizeKaalaName('purvahna')]: 'puurvaahna',
  [normalizeKaalaName('मध्याह्नः')]: 'madhyaahna',
  [normalizeKaalaName('madhyaahna')]: 'madhyaahna',
  [normalizeKaalaName('madhyahna')]: 'madhyaahna',
  [normalizeKaalaName('सायाह्नः')]: 'saayaahna',
  [normalizeKaalaName('सायाह्न')]: 'saayaahna',
  [normalizeKaalaName('saayaahna')]: 'saayaahna',
  [normalizeKaalaName('sayahna')]: 'saayaahna',
  [normalizeKaalaName('सायम्')]: 'saayaahna',
  [normalizeKaalaName('सायम')]: 'saayaahna',
  [normalizeKaalaName('saayam')]: 'saayaahna',
  [normalizeKaalaName('sayam')]: 'saayaahna',
  [normalizeKaalaName('sayana')]: 'saayaahna',
  [normalizeKaalaName('साङ्गवः')]: 'saangava',
  [normalizeKaalaName('साङ्गव')]: 'saangava',
  [normalizeKaalaName('saangava')]: 'saangava',
  [normalizeKaalaName('सङ्गवः')]: 'saangava',
  [normalizeKaalaName('सङ्गव')]: 'saangava',
  [normalizeKaalaName('sangava')]: 'saangava',
  [normalizeKaalaName('प्रातः')]: 'praatah',
  [normalizeKaalaName('praatah')]: 'praatah',
  [normalizeKaalaName('pratah')]: 'praatah',
  [normalizeKaalaName('prata_kalidigyanam.pratah')]: 'praatah',
  [normalizeKaalaName('prata_kalidigyanam.praatah')]: 'praatah',
  [normalizeKaalaName('prata_kalidigyanam.1')]: 'praatah',
  [normalizeKaalaName('prata_kalidigyanam.sangava')]: 'saangava',
  [normalizeKaalaName('prata_kalidigyanam.saangava')]: 'saangava',
  [normalizeKaalaName('prata_kalidigyanam.2')]: 'saangava',
  [normalizeKaalaName('prata_kalidigyanam.madhyaahna')]: 'madhyaahna',
  [normalizeKaalaName('prata_kalidigyanam.3')]: 'madhyaahna',
  [normalizeKaalaName('prata_kalidigyanam.aparaahna')]: 'aparaahna',
  [normalizeKaalaName('prata_kalidigyanam.4')]: 'aparaahna',
  [normalizeKaalaName('prata_kalidigyanam.saayam')]: 'saayaahna',
  [normalizeKaalaName('prata_kalidigyanam.sayahna')]: 'saayaahna',
  [normalizeKaalaName('prata_kalidigyanam.5')]: 'saayaahna',
  [normalizeKaalaName('प्रदोषः')]: 'pradosha',
  [normalizeKaalaName('प्रदोषकालः')]: 'pradosha',
  [normalizeKaalaName('प्रदोष काल')]: 'pradosha',
  [normalizeKaalaName('pradosha')]: 'pradosha',
  [normalizeKaalaName('pradosh')]: 'pradosha',
  [normalizeKaalaName('pradosh kaala')]: 'pradosha',
  [normalizeKaalaName('pradosha kaala')]: 'pradosha',
  [normalizeKaalaName('pradosh kala')]: 'pradosha',
  [normalizeKaalaName('pradosha kala')]: 'pradosha',
  [normalizeKaalaName('निशीथः')]: 'nishiitha',
  [normalizeKaalaName('nishiitha')]: 'nishiitha',
  [normalizeKaalaName('nishitha')]: 'nishiitha',
  [normalizeKaalaName('मध्यरात्रिः')]: 'madhyaraatri',
  [normalizeKaalaName('madhyaraatri')]: 'madhyaraatri',
  [normalizeKaalaName('madhyaratri')]: 'madhyaraatri',
  [normalizeKaalaName('रात्रिमानम्')]: 'raatrimaanam',
  [normalizeKaalaName('raatrimaanam')]: 'raatrimaanam',
  [normalizeKaalaName('ratrimanam')]: 'raatrimaanam',
  [normalizeKaalaName('ब्राह्ममुहूर्तः')]: 'braahma',
  [normalizeKaalaName('braahma')]: 'braahma',
  [normalizeKaalaName('brahma')]: 'braahma',
  [normalizeKaalaName('brahma muhurta')]: 'braahma',
  [normalizeKaalaName('राहुकालः')]: 'raahu',
  [normalizeKaalaName('raahu')]: 'raahu',
  [normalizeKaalaName('raahu kaala')]: 'raahu',
  [normalizeKaalaName('rahu kala')]: 'raahu',
  [normalizeKaalaName('सूर्यास्तमयः')]: 'sunset',
  [normalizeKaalaName('सूर्यास्त')]: 'sunset',
  [normalizeKaalaName('sunset')]: 'sunset',
  [normalizeKaalaName('sunset_time')]: 'sunset',
  [normalizeKaalaName('सूर्योदयः')]: 'sunrise',
  [normalizeKaalaName('सूर्योदय')]: 'sunrise',
  [normalizeKaalaName('sunrise')]: 'sunrise',
  [normalizeKaalaName('sunrise_time')]: 'sunrise',
  [normalizeKaalaName('yesterday_sunset_time')]: 'yesterday_sunset_time',
  [normalizeKaalaName('tomorrow_sunrise_time')]: 'tomorrow_sunrise_time',
};

export function kaalaObjectKey(value: string | undefined | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const mapped = KAALA_NAME_TO_KEY[normalizeKaalaName(trimmed)];
  if (mapped) return mapped;
  const leaf = trimmed.includes('.') ? trimmed.split('.').pop()! : trimmed;
  const leafMapped = KAALA_NAME_TO_KEY[normalizeKaalaName(leaf)];
  if (leafMapped) return leafMapped;
  if (/^[a-z_]+$/i.test(leaf)) return leaf.toLowerCase();
  if (/^[a-z_.]+$/i.test(trimmed)) return trimmed.toLowerCase();
  return undefined;
}

function dayLengthPeriods(day: PanchangaYearDay | undefined): Record<string, unknown> | null {
  if (!day) return null;
  const coordinates = isRecord(day.coordinates) ? day.coordinates : null;
  const fromCoordinates = coordinates?.day_length_based_periods;
  if (isRecord(fromCoordinates)) return fromCoordinates;
  const direct = day.day_length_based_periods;
  return isRecord(direct) ? direct : null;
}

function addPeriod(
  byName: Map<string, PanchangaKaalaPeriod>,
  byKey: Map<string, PanchangaKaalaPeriod>,
  name: unknown,
  start: unknown,
  end: unknown,
  objectKey?: string,
): void {
  if (typeof start !== 'string' || typeof end !== 'string') return;
  const trimmedStart = start.trim();
  const trimmedEnd = end.trim();
  if (!trimmedStart || !trimmedEnd) return;

  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (trimmedName.includes('मु॰')) return;
  const displayName = trimmedName || objectKey || '';
  if (!displayName) return;

  const period: PanchangaKaalaPeriod = {
    name: displayName,
    key: objectKey,
    start: trimmedStart,
    end: trimmedEnd,
  };

  if (trimmedName && !byName.has(trimmedName)) byName.set(trimmedName, period);
  if (objectKey && !byKey.has(objectKey)) byKey.set(objectKey, period);
}

function addNamedInterval(
  byName: Map<string, PanchangaKaalaPeriod>,
  byKey: Map<string, PanchangaKaalaPeriod>,
  value: unknown,
  objectKey?: string,
): void {
  if (!isRecord(value)) return;
  addPeriod(byName, byKey, value.name, value.local_start_time, value.local_end_time, objectKey);
}

function collectTree(
  byName: Map<string, PanchangaKaalaPeriod>,
  byKey: Map<string, PanchangaKaalaPeriod>,
  value: unknown,
  objectKey?: string,
): void {
  if (Array.isArray(value)) {
    value.forEach((item) => collectTree(byName, byKey, item, objectKey));
    return;
  }
  if (!isRecord(value)) return;
  addNamedInterval(byName, byKey, value, objectKey);
  Object.entries(value).forEach(([key, nested]) => {
    if (key === 'jsonClass') return;
    collectTree(byName, byKey, nested, key);
  });
}

function intervalFromNode(value: unknown, objectKey?: string): PanchangaKaalaPeriod | null {
  if (!isRecord(value)) return null;
  if (typeof value.local_time === 'string' && value.local_time.trim()) {
    const t = value.local_time.trim();
    return {
      name: typeof value.name === 'string' ? value.name.trim() : (objectKey || ''),
      key: objectKey,
      start: t,
      end: t,
    };
  }
  const start =
    value.local_start_time ??
    value.start_time_full ??
    value.start_time ??
    value.start;
  const end =
    value.local_end_time ??
    value.end_time_full ??
    value.end_time ??
    value.end;
  if (typeof start !== 'string' || typeof end !== 'string') return null;
  const name = typeof value.name === 'string' ? value.name.trim() : '';
  if (name.includes('मु॰')) return null;
  const trimmedStart = start.trim();
  const trimmedEnd = end.trim();
  if (!trimmedStart || !trimmedEnd) return null;
  return {
    name: name || objectKey || '',
    key: objectKey,
    start: trimmedStart,
    end: trimmedEnd,
  };
}

function parseTimeToSeconds(timeStr: string | undefined): number | null {
  if (!timeStr) return null;
  const parts = timeStr.trim().split(':').map(Number);
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const [h, m, s = 0] = parts;
  return h * 3600 + m * 60 + s;
}

function formatDateTime(dateStr: string, totalSec: number): string {
  const daysOffset = Math.floor(totalSec / 86400);
  const secInDay = totalSec - daysOffset * 86400;
  let targetDateStr = dateStr;
  if (daysOffset !== 0) {
    const d = new Date(`${dateStr}T12:00:00`);
    d.setDate(d.getDate() + daysOffset);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayNum = String(d.getDate()).padStart(2, '0');
    targetDateStr = `${y}-${m}-${dayNum}`;
  }
  const norm = ((Math.floor(secInDay) % 86400) + 86400) % 86400;
  const h = Math.floor(norm / 3600);
  const min = Math.floor((norm % 3600) / 60);
  const s = norm % 60;
  return `${targetDateStr} ${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

type KaalaPeriodIndex = {
  byName: Map<string, PanchangaKaalaPeriod>;
  byKey: Map<string, PanchangaKaalaPeriod>;
};

function populateSynthesizedVedicKaalas(
  byName: Map<string, PanchangaKaalaPeriod>,
  byKey: Map<string, PanchangaKaalaPeriod>,
  day: PanchangaYearDay,
): void {
  const dateKey = typeof day.date === 'string' ? day.date.trim() : '';
  if (!dateKey) return;

  const sr = parseTimeToSeconds(typeof day.sunrise === 'string' ? day.sunrise : undefined) ?? 6 * 3600;
  let ss = parseTimeToSeconds(typeof day.sunset === 'string' ? day.sunset : undefined) ?? 18 * 3600;
  if (ss < sr) ss += 86400;
  const D = ss - sr;
  const N = 86400 - D;
  const nextSr = ss + N;

  const synth: Array<{ name: string; key: string; startSec: number; endSec: number }> = [
    { name: 'प्रातः', key: 'praatah', startSec: sr, endSec: sr + 0.2 * D },
    { name: 'साङ्गवः', key: 'saangava', startSec: sr + 0.2 * D, endSec: sr + 0.4 * D },
    { name: 'मध्याह्नः', key: 'madhyaahna', startSec: sr + 0.4 * D, endSec: sr + 0.6 * D },
    { name: 'अपराह्णः', key: 'aparaahna', startSec: sr + 0.6 * D, endSec: sr + 0.8 * D },
    { name: 'सायाह्नः', key: 'saayaahna', startSec: sr + 0.8 * D, endSec: ss },
    { name: 'पूर्वाह्णः', key: 'puurvaahna', startSec: sr, endSec: sr + 0.5 * D },
    { name: 'प्रदोषः', key: 'pradosha', startSec: ss, endSec: ss + 0.2 * N },
    { name: 'निशीथः', key: 'nishiitha', startSec: ss + 0.4 * N, endSec: ss + 0.6 * N },
    { name: 'मध्यरात्रिः', key: 'madhyaraatri', startSec: ss + 0.5 * N, endSec: ss + 0.5 * N },
    { name: 'रात्रिमानम्', key: 'raatrimaanam', startSec: ss, endSec: nextSr },
    { name: 'पूर्वरात्रिः~(त्रेधा)', key: 'puurvaraatri', startSec: ss, endSec: ss + (1 / 3) * N },
    { name: 'प्राक्तनारुणोदयः', key: 'praaktanaruunodaya', startSec: sr - 5760, endSec: sr },
  ];

  synth.forEach(({ name, key, startSec, endSec }) => {
    if (!byKey.has(key) && !byName.has(name)) {
      addPeriod(byName, byKey, name, formatDateTime(dateKey, startSec), formatDateTime(dateKey, endSec), key);
    }
  });
}

function indexKaalaPeriods(day: PanchangaYearDay | undefined): KaalaPeriodIndex {
  const byName = new Map<string, PanchangaKaalaPeriod>();
  const byKey = new Map<string, PanchangaKaalaPeriod>();
  if (!day) return { byName, byKey };

  const dateKey = typeof day.date === 'string' ? day.date.trim() : '';
  if (dateKey) {
    INSTANT_KAALA_FIELDS.forEach(({ name, field }) => {
      const time = day[field];
      if (typeof time !== 'string' || !time.trim()) return;
      addPeriod(
        byName,
        byKey,
        name,
        `${dateKey} ${time.trim()}`,
        `${dateKey} ${time.trim()}`,
        field,
      );
      if (field === 'sunset') {
        addPeriod(byName, byKey, name, `${dateKey} ${time.trim()}`, `${dateKey} ${time.trim()}`, 'sunset_time');
      }
      if (field === 'sunrise') {
        addPeriod(byName, byKey, name, `${dateKey} ${time.trim()}`, `${dateKey} ${time.trim()}`, 'sunrise_time');
      }
    });
  }

  const pDay = (isRecord((day as any).panchanga) ? (day as any).panchanga : day) as PanchangaYearDay;
  const coordinates = (
    isRecord((day as any).coordinates)
      ? (day as any).coordinates
      : isRecord(pDay?.coordinates)
        ? pDay.coordinates
        : pDay
  ) as Record<string, unknown> | null;

  if (coordinates) {
    const timeFields: Array<{ name: string; key: string; field: string }> = [
      { name: 'सूर्योदयः', key: 'sunrise_time', field: 'sunrise_time' },
      { name: 'सूर्यास्तमयः', key: 'sunset_time', field: 'sunset_time' },
      { name: 'हिजोको सूर्यास्त', key: 'yesterday_sunset_time', field: 'yesterday_sunset_time' },
      { name: 'भोलिको सूर्योदय', key: 'tomorrow_sunrise_time', field: 'tomorrow_sunrise_time' },
    ];
    timeFields.forEach(({ name, key, field }) => {
      const node = coordinates[field];
      if (isRecord(node) && typeof node.local_time === 'string' && node.local_time.trim()) {
        const t = node.local_time.trim();
        addPeriod(byName, byKey, name, t, t, key);
        if (key === 'sunset_time') {
          addPeriod(byName, byKey, name, t, t, 'sunset');
        }
        if (key === 'sunrise_time') {
          addPeriod(byName, byKey, name, t, t, 'sunrise');
        }
      }
    });

    if (Array.isArray(coordinates.prata_kalidigyanam)) {
      coordinates.prata_kalidigyanam.forEach((item: any) => {
        if (item && typeof item === 'object') {
          const start = item.start_time_full || item.start_time;
          const end = item.end_time_full || item.end_time;
          const name = item.name || '';
          if (name && start && end) {
            const subKey =
              item.index === 1 ? 'pratah' :
              item.index === 2 ? 'sangava' :
              item.index === 3 ? 'madhyaahna' :
              item.index === 4 ? 'aparaahna' :
              item.index === 5 ? 'saayam' : `item_${item.index}`;
            addPeriod(byName, byKey, name, start, end, `prata_kalidigyanam.${subKey}`);
            addPeriod(byName, byKey, name, start, end, subKey);
          }
        }
      });
    }

    if (isRecord(coordinates.kulikaadi_bela)) {
      Object.entries(coordinates.kulikaadi_bela).forEach(([k, item]: [string, any]) => {
        if (item && typeof item === 'object') {
          const start = item.start_time_full || item.start_time;
          const end = item.end_time_full || item.end_time;
          if (start && end) {
            addPeriod(byName, byKey, item.name || k, start, end, `kulikaadi_bela.${k}`);
            addPeriod(byName, byKey, item.name || k, start, end, k);
          }
        }
      });
    }
  }

  const periods = dayLengthPeriods(day);
  if (periods) {
    Object.entries(periods).forEach(([key, value]) => {
      if (NESTED_DIVISION_KEYS.has(key) || key === 'jsonClass') return;
      addNamedInterval(byName, byKey, value, key);
    });

    // 15-fold first so अपराह्णः → aparaahna wins over an 8-fold homonym.
    collectTree(byName, byKey, periods.fifteen_fold_division);
    collectTree(byName, byKey, periods.eight_fold_division);
  }

  // Synthesize standard Vedic Kaalas for any missing standard keys
  populateSynthesizedVedicKaalas(byName, byKey, day);

  return { byName, byKey };
}

/** Named kaala windows for a panchanga day (first match wins: top-level, then 15-fold, then 8-fold). */
export function extractKaalaPeriods(day: PanchangaYearDay | undefined): PanchangaKaalaPeriod[] {
  const { byName, byKey } = indexKaalaPeriods(day);
  const seen = new Set<PanchangaKaalaPeriod>();
  const list: PanchangaKaalaPeriod[] = [];
  for (const period of Array.from(byName.values()).concat(Array.from(byKey.values()))) {
    if (seen.has(period)) continue;
    seen.add(period);
    list.push(period);
  }
  return list;
}

function parseDateTimeMs(str: string): number {
  if (!str) return NaN;
  const trimmed = str.trim();
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) {
    return new Date(trimmed).getTime();
  }
  const datePart = trimmed.slice(0, spaceIdx);
  const timePart = trimmed.slice(spaceIdx + 1);
  const timePieces = timePart.split(':').map(Number);
  if (timePieces.length >= 2 && timePieces.every((n) => Number.isFinite(n))) {
    const [h, m, s = 0] = timePieces;
    const daysOffset = Math.floor(h / 24);
    const normH = h % 24;
    const dateObj = new Date(`${datePart}T12:00:00`);
    if (!Number.isNaN(dateObj.getTime())) {
      dateObj.setDate(dateObj.getDate() + daysOffset);
      const y = dateObj.getFullYear();
      const mo = String(dateObj.getMonth() + 1).padStart(2, '0');
      const da = String(dateObj.getDate()).padStart(2, '0');
      const normHStr = String(normH).padStart(2, '0');
      const normMStr = String(m).padStart(2, '0');
      const normSStr = String(s).padStart(2, '0');
      return new Date(`${y}-${mo}-${da}T${normHStr}:${normMStr}:${normSStr}`).getTime();
    }
  }
  const iso = trimmed.replace(' ', 'T');
  return new Date(iso).getTime();
}

function resolveCoordPath(root: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = root;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!cur || typeof cur !== 'object') return undefined;
    if (Array.isArray(cur)) {
      const idx = Number(part);
      if (Number.isFinite(idx)) {
        cur = cur[idx] || cur.find((x: any) => x?.index === idx || x?.number === idx);
      } else {
        const prataMap: Record<string, number> = {
          pratah: 1, 'प्रातः': 1,
          sangava: 2, saangava: 2, 'सङ्गवः': 2, 'साङ्गवः': 2,
          madhyaahna: 3, 'मध्याह्नः': 3,
          aparaahna: 4, 'अपराह्णः': 4,
          saayam: 5, sayana: 5, 'सायम्': 5, 'सायम': 5,
        };
        const mappedIdx = prataMap[part.toLowerCase()] ?? prataMap[part];
        if (mappedIdx != null) {
          cur = cur.find((x: any) => x?.index === mappedIdx);
        } else {
          cur = cur.find((x: any) =>
            normalizeKaalaName(String(x?.name || x?.key || x?.name_sa || x?.name_en || '')) === normalizeKaalaName(part)
          );
        }
      }
    } else if (part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return cur;
}

function lookupKaalaByPath(
  day: PanchangaYearDay | undefined,
  pathOrKey: string,
): PanchangaKaalaPeriod | null {
  if (!day) return null;
  const rawPath = pathOrKey.trim();
  if (!rawPath) return null;

  const pDay = (isRecord((day as any).panchanga) ? (day as any).panchanga : day) as PanchangaYearDay;
  const coordinates = (
    isRecord((day as any).coordinates)
      ? (day as any).coordinates
      : isRecord(pDay?.coordinates)
        ? pDay.coordinates
        : pDay
  ) as Record<string, unknown> | null;
  const dayLength = dayLengthPeriods(pDay);
  const cleanPath = rawPath.replace(/^(coordinates\.|day_length_based_periods\.)+/, '');

  const leafKey = cleanPath.split('.').pop() || cleanPath;
  const mappedKey = kaalaObjectKey(cleanPath) || kaalaObjectKey(leafKey) || leafKey;

  const candidates = [
    coordinates ? resolveCoordPath(coordinates, rawPath) : undefined,
    coordinates ? resolveCoordPath(coordinates, cleanPath) : undefined,
    coordinates ? resolveCoordPath(coordinates, mappedKey) : undefined,
    coordinates && (coordinates as any).day_length_based_periods ? resolveCoordPath((coordinates as any).day_length_based_periods, cleanPath) : undefined,
    coordinates && (coordinates as any).day_length_based_periods ? resolveCoordPath((coordinates as any).day_length_based_periods, mappedKey) : undefined,
    dayLength ? resolveCoordPath(dayLength, rawPath) : undefined,
    dayLength ? resolveCoordPath(dayLength, cleanPath) : undefined,
    dayLength ? resolveCoordPath(dayLength, mappedKey) : undefined,
  ];

  for (const cand of candidates) {
    const period = intervalFromNode(cand, cleanPath.split('.').pop());
    if (period) return period;
  }

  if (dayLength) {
    const fifteen = isRecord(dayLength.fifteen_fold_division)
      ? intervalFromNode(
          dayLength.fifteen_fold_division[leafKey] ?? (mappedKey ? dayLength.fifteen_fold_division[mappedKey] : undefined),
          leafKey,
        )
      : null;
    if (fifteen) return fifteen;
    const eight = isRecord(dayLength.eight_fold_division)
      ? intervalFromNode(
          dayLength.eight_fold_division[leafKey] ?? (mappedKey ? dayLength.eight_fold_division[mappedKey] : undefined),
          leafKey,
        )
      : null;
    if (eight) return eight;
    const direct = intervalFromNode(
      dayLength[leafKey] ?? (mappedKey ? dayLength[mappedKey] : undefined),
      leafKey,
    );
    if (direct) return direct;
  }

  return null;
}

export function findKaalaPeriod(
  day: PanchangaYearDay | Record<string, unknown> | undefined,
  kaala: string | undefined | null,
): PanchangaKaalaPeriod | null {
  const wanted = kaala?.trim();
  if (!day || !wanted) return null;

  const pDay = day as PanchangaYearDay;

  // 1. Direct path lookup in coordinates or dayLengthPeriods
  const fromDirect = lookupKaalaByPath(pDay, wanted);
  if (fromDirect) return fromDirect;

  const objectKey = kaalaObjectKey(wanted);
  if (objectKey) {
    const fromPath = lookupKaalaByPath(pDay, objectKey);
    if (fromPath) return fromPath;
  }

  const { byName, byKey } = indexKaalaPeriods(pDay);
  if (objectKey) {
    const fromKey = byKey.get(objectKey);
    if (fromKey) return fromKey;
  }

  const exact = byName.get(wanted);
  if (exact) return exact;

  const normalized = normalizeKaalaName(wanted);
  if (normalized) {
    const fromNormKey = byKey.get(normalized);
    if (fromNormKey) return fromNormKey;

    const named = Array.from(byName.values());
    for (let i = 0; i < named.length; i += 1) {
      if (normalizeKaalaName(named[i].name) === normalized) return named[i];
    }
  }

  return null;
}

function isTithiIndexMatch(
  itemIndex: number,
  itemPakshya: string | undefined,
  dayPakshya: string | undefined,
  targetTithi30: number,
): boolean {
  if (!Number.isFinite(itemIndex) || !Number.isFinite(targetTithi30)) return false;
  if (itemIndex === targetTithi30) return true;
  if (itemIndex <= 15) {
    const isKrishna =
      isPakshyaKrishna(itemPakshya) || (!itemPakshya && isPakshyaKrishna(dayPakshya));
    const normalized30 = isKrishna ? itemIndex + 15 : itemIndex;
    if (normalized30 === targetTithi30) return true;
    if (targetTithi30 > 15 && targetTithi30 - 15 === itemIndex) return true;
    if (targetTithi30 <= 15 && targetTithi30 === itemIndex) return true;
  }
  if (targetTithi30 <= 15 && itemIndex > 15 && itemIndex - 15 === targetTithi30) {
    return true;
  }
  return false;
}

/**
 * Checks whether a target tithi (30-cycle index 1..30) was active during the specified kaala window.
 */
export function isTithiActiveAtKaala(
  day: PanchangaYearDay | Record<string, unknown> | undefined,
  targetTithi30: number,
  kaala: string | undefined | null,
): boolean {
  if (!day || !targetTithi30) return false;
  const kaalaPeriod = findKaalaPeriod(day as PanchangaYearDay, kaala);
  if (!kaalaPeriod) return false;

  const kStart = parseDateTimeMs(kaalaPeriod.start);
  const kEnd = parseDateTimeMs(kaalaPeriod.end);

  const pDay = (isRecord((day as any).panchanga) ? (day as any).panchanga : day) as Record<string, unknown>;
  const coordinates = (isRecord((day as any).coordinates) ? (day as any).coordinates : pDay.coordinates) as Record<string, unknown> | undefined;
  const dayPakshya = typeof pDay.pakshya === 'string' ? pDay.pakshya : (typeof (day as any).pakshya === 'string' ? (day as any).pakshya : undefined);
  const baseDate = typeof pDay.date === 'string' ? pDay.date.trim() : (typeof (day as any).date === 'string' ? (day as any).date.trim() : '');

  const checkTimesActive = (startStrRaw?: string | null, endStrRaw?: string | null): boolean => {
    let startStr = typeof startStrRaw === 'string' && startStrRaw.trim() ? startStrRaw.trim() : '';
    let endStr = typeof endStrRaw === 'string' && endStrRaw.trim() ? endStrRaw.trim() : '';

    if (startStr && !startStr.includes(' ') && baseDate) startStr = `${baseDate} ${startStr}`;
    if (endStr && !endStr.includes(' ') && baseDate) endStr = `${baseDate} ${endStr}`;

    const tStart = startStr ? parseDateTimeMs(startStr) : -Infinity;
    const tEnd = endStr ? parseDateTimeMs(endStr) : Infinity;

    if (!Number.isNaN(kEnd) && !Number.isNaN(tStart) && tStart >= kEnd) return false;
    if (!Number.isNaN(kStart) && !Number.isNaN(tEnd) && tEnd <= kStart) return false;

    return true;
  };

  let foundTimingForTarget = false;

  // 1. Check coordinates.tithis_in_day
  const tithisInDay = Array.isArray(coordinates?.tithis_in_day)
    ? coordinates!.tithis_in_day
    : Array.isArray(pDay.tithis_in_day)
      ? pDay.tithis_in_day
      : Array.isArray((day as any).tithis_in_day)
        ? (day as any).tithis_in_day
        : [];

  if (tithisInDay.length > 0) {
    for (const t of tithisInDay) {
      if (t && typeof t === 'object') {
        const idx = Number((t as any).index ?? (t as any).tithi_index);
        const itemPakshya = typeof (t as any).pakshya === 'string' ? (t as any).pakshya : undefined;
        if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
          foundTimingForTarget = true;
          if (checkTimesActive((t as any).start_time, (t as any).end_time)) return true;
        }
      }
    }
  }

  // 2. Check events
  const events = Array.isArray(pDay.events)
    ? pDay.events
    : Array.isArray((day as any).events)
      ? (day as any).events
      : [];

  const tithiEvents = events.filter(
    (item: any): item is PanchangaEventRow & { start?: string | null; end?: string | null; pakshya?: string } =>
      Boolean(item) && typeof item === 'object' && item.kind === 'tithi'
  );

  if (tithiEvents.length > 0) {
    for (const ev of tithiEvents) {
      const idx = Number(ev.index);
      const itemPakshya = typeof ev.pakshya === 'string' ? ev.pakshya : undefined;
      if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
        foundTimingForTarget = true;
        const sTime = ev.start ?? (ev as any).start_time ?? (ev as any).start_time_full;
        const eTime = ev.end ?? (ev as any).end_time ?? (ev as any).end_time_full;
        if (checkTimesActive(sTime, eTime)) return true;
      }
    }
  }

  // 3. Check tithi_transitions
  const rawTransitions = Array.isArray(pDay.tithi_transitions)
    ? pDay.tithi_transitions
    : Array.isArray((day as any).tithi_transitions)
      ? (day as any).tithi_transitions
      : [];

  if (rawTransitions.length > 0) {
    for (const t of rawTransitions) {
      if (t && typeof t === 'object') {
        const idx = Number((t as any).tithi_index ?? (t as any).index);
        const itemPakshya = typeof (t as any).pakshya === 'string' ? (t as any).pakshya : undefined;
        if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
          foundTimingForTarget = true;
          const sTime = (t as any).start_time ?? (t as any).start_time_full ?? (t as any).start;
          const eTime = (t as any).end_time ?? (t as any).end_time_full ?? (t as any).end;
          if (checkTimesActive(sTime, eTime)) return true;
        }
      }
    }
  }

  if (foundTimingForTarget) return false;

  const tithiIndex = Number(pDay.tithi_index ?? (day as any).tithi_index);
  return Number.isFinite(tithiIndex) && isTithiIndexMatch(tithiIndex, undefined, dayPakshya, targetTithi30);
}

/**
 * Calculates the overlap duration in minutes between a target tithi (30-cycle index 1..30)
 * and the specified kaala window on a day.
 */
export function getTithiKaalaOverlapMinutes(
  day: PanchangaYearDay | Record<string, unknown> | undefined,
  targetTithi30: number,
  kaala: string | undefined | null,
): number {
  if (!day || !targetTithi30) return 0;
  const kaalaPeriod = findKaalaPeriod(day as PanchangaYearDay, kaala);
  if (!kaalaPeriod) return 0;

  const kStart = parseDateTimeMs(kaalaPeriod.start);
  const kEnd = parseDateTimeMs(kaalaPeriod.end);
  if (Number.isNaN(kStart) || Number.isNaN(kEnd) || kEnd <= kStart) {
    return isTithiActiveAtKaala(day, targetTithi30, kaala) ? 1 : 0;
  }

  const pDay = (isRecord((day as any).panchanga) ? (day as any).panchanga : day) as Record<string, unknown>;
  const coordinates = (isRecord((day as any).coordinates) ? (day as any).coordinates : pDay.coordinates) as Record<string, unknown> | undefined;
  const dayPakshya = typeof pDay.pakshya === 'string' ? pDay.pakshya : (typeof (day as any).pakshya === 'string' ? (day as any).pakshya : undefined);
  const baseDate = typeof pDay.date === 'string' ? pDay.date.trim() : (typeof (day as any).date === 'string' ? (day as any).date.trim() : '');

  let maxOverlapMs = 0;

  const checkInterval = (startRaw?: string | null, endRaw?: string | null) => {
    let sStr = typeof startRaw === 'string' && startRaw.trim() ? startRaw.trim() : '';
    let eStr = typeof endRaw === 'string' && endRaw.trim() ? endRaw.trim() : '';
    if (sStr && !sStr.includes(' ') && baseDate) sStr = `${baseDate} ${sStr}`;
    if (eStr && !eStr.includes(' ') && baseDate) eStr = `${baseDate} ${eStr}`;

    const tStart = sStr ? parseDateTimeMs(sStr) : -Infinity;
    const tEnd = eStr ? parseDateTimeMs(eStr) : Infinity;

    const overlapStart = Math.max(kStart, Number.isNaN(tStart) ? kStart : tStart);
    const overlapEnd = Math.min(kEnd, Number.isNaN(tEnd) ? kEnd : tEnd);

    if (overlapEnd > overlapStart) {
      const dur = overlapEnd - overlapStart;
      if (dur > maxOverlapMs) maxOverlapMs = dur;
    }
  };

  // 1. Check coordinates.tithis_in_day
  const tithisInDay = Array.isArray(coordinates?.tithis_in_day)
    ? coordinates!.tithis_in_day
    : Array.isArray(pDay.tithis_in_day)
      ? pDay.tithis_in_day
      : Array.isArray((day as any).tithis_in_day)
        ? (day as any).tithis_in_day
        : [];

  let foundInterval = false;
  if (tithisInDay.length > 0) {
    for (const t of tithisInDay) {
      if (t && typeof t === 'object') {
        const idx = Number((t as any).index ?? (t as any).tithi_index);
        const itemPakshya = typeof (t as any).pakshya === 'string' ? (t as any).pakshya : undefined;
        if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
          checkInterval(
            (t as any).start_time ?? (t as any).start_time_full ?? (t as any).start,
            (t as any).end_time ?? (t as any).end_time_full ?? (t as any).end,
          );
          foundInterval = true;
        }
      }
    }
  }

  // 2. Check events
  const events = Array.isArray(pDay.events)
    ? pDay.events
    : Array.isArray((day as any).events)
      ? (day as any).events
      : [];

  const tithiEvents = events.filter(
    (item: any): item is PanchangaEventRow & { start?: string | null; end?: string | null; pakshya?: string } =>
      Boolean(item) && typeof item === 'object' && item.kind === 'tithi'
  );

  if (tithiEvents.length > 0) {
    for (const ev of tithiEvents) {
      const idx = Number(ev.index);
      const itemPakshya = typeof ev.pakshya === 'string' ? ev.pakshya : undefined;
      if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
        checkInterval(
          ev.start ?? (ev as any).start_time ?? (ev as any).start_time_full,
          ev.end ?? (ev as any).end_time ?? (ev as any).end_time_full,
        );
        foundInterval = true;
      }
    }
  }

  // 3. Check tithi_transitions
  if (!foundInterval) {
    const rawTransitions = Array.isArray(pDay.tithi_transitions)
      ? pDay.tithi_transitions
      : Array.isArray((day as any).tithi_transitions)
        ? (day as any).tithi_transitions
        : [];

    for (const t of rawTransitions) {
      if (t && typeof t === 'object') {
        const idx = Number((t as any).tithi_index ?? (t as any).index);
        const itemPakshya = typeof (t as any).pakshya === 'string' ? (t as any).pakshya : undefined;
        if (isTithiIndexMatch(idx, itemPakshya, dayPakshya, targetTithi30)) {
          checkInterval(
            (t as any).start_time ?? (t as any).start_time_full ?? (t as any).start,
            (t as any).end_time ?? (t as any).end_time_full ?? (t as any).end,
          );
        }
      }
    }
  }

  return maxOverlapMs > 0 ? maxOverlapMs / 60000 : (isTithiActiveAtKaala(day, targetTithi30, kaala) ? 1 : 0);
}

